import {
  ANNOTATION_EDIT_URL,
  ANNOTATION_LOCATION,
  ANNOTATION_SOURCE_LOCATION,
  stringifyEntityRef,
  parseLocationRef,
  isComponentEntity,
} from '@backstage/catalog-model';
import { EntitySwitch } from '@backstage/plugin-catalog';
import { InfoCard, Link } from '@backstage/core-components';
import { alertApiRef, useApi } from '@backstage/core-plugin-api';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import { IconButton, makeStyles, Typography } from '@material-ui/core';
import CachedIcon from '@material-ui/icons/Cached';
import EditIcon from '@material-ui/icons/Edit';
import GitHubIcon from '@material-ui/icons/GitHub';
import { useCallback } from 'react';
import { AboutContent } from './AboutContent';

const useStyles = makeStyles({
  gridItemCard: {
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100% - 10px)', // for pages without content header
    marginBottom: '10px',
  },
  gridItemCardContent: {
    flex: 1,
    margin: '10px',
  },
  headerCard: {
    marginBottom: '10px',
  },
});

/**
 * Exported publicly via the EntityAboutCard
 */
export function AboutCard() {
  const classes = useStyles();
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const alertApi = useApi(alertApiRef);

  const repoLocation =
    entity.metadata.annotations?.[ANNOTATION_SOURCE_LOCATION] &&
    parseLocationRef(entity.metadata.annotations?.[ANNOTATION_SOURCE_LOCATION]!)
      .target;
  const entityMetadataEditUrl =
    entity.metadata.annotations?.[ANNOTATION_EDIT_URL];
  const entityLocation = entity.metadata.annotations?.[ANNOTATION_LOCATION];

  // Limiting the ability to manually refresh to the less expensive locations
  const allowRefresh =
    entityLocation?.startsWith('url:') || entityLocation?.startsWith('file:');
  const refreshEntity = useCallback(async () => {
    await catalogApi.refreshEntity(stringifyEntityRef(entity));
    alertApi.post({ message: 'Refresh scheduled', severity: 'info' });
  }, [catalogApi, alertApi, entity]);

  return (
    <InfoCard
      title="About"
      className={classes.headerCard}
      action={
        <>
          {allowRefresh && (
            <IconButton
              aria-label="Refresh"
              title="Schedule entity refresh"
              onClick={refreshEntity}
            >
              <CachedIcon />
            </IconButton>
          )}

          <IconButton
            component={Link}
            aria-label="View"
            disabled={!repoLocation}
            title="Go to GitHub"
            to={repoLocation ?? '#'}
          >
            <GitHubIcon />
          </IconButton>

          <IconButton
            component={Link}
            aria-label="Edit"
            disabled={!entityMetadataEditUrl}
            title="Edit Metadata"
            to={entityMetadataEditUrl ?? '#'}
          >
            <EditIcon />
          </IconButton>
        </>
      }
    >
      <EntitySwitch>
        <EntitySwitch.Case if={isComponentEntity}>
          <Typography variant="subtitle1">
            {entity.metadata.description} <br />
            <br />
          </Typography>
        </EntitySwitch.Case>
      </EntitySwitch>
      <AboutContent entity={entity} />
    </InfoCard>
  );
}
